import React, { useRef, useState } from "react";
import { Button, Card, Input, Menu, Text } from "@fragments";
import {
  IconArrowUp,
  IconAspectRatio,
  IconFocus2,
  IconLayoutGrid,
  IconWand,
  IconMicrophone,
  IconPhotoPlus,
  IconPlayerStopFilled,
  IconPlus,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import FormatSelector from "../FormatSelector";
import PersonalityButton from "../PersonalityButton";
import TopicToken from "../TopicToken";
import ArtToken from "../ArtToken";
import ComposerAttachments from "../ComposerAttachments";
import { ART_DEFAULT_RATIO, DEFAULT_FORMAT, STUDIO_FORMATS } from "../../../../enums/formats";
import { ATTACHMENT_TYPES } from "../../../../enums/attachments";
import AudioRecorder from "../../../AudioRecorder";
import useCapture from "../../../../hooks/useCapture";
import useInlineCapture from "../../../../hooks/useInlineCapture";
import useDraft from "../../../../hooks/useDraft";
import useVoiceDraft from "./hooks/useVoiceDraft";
import "./styles.scss";

const VOICE_STATUS = {
  transcribing: "transcribing",
  ready: "audioTranscribed",
  failed: "error_transcription",
};

export default function ComposerInput({
  studio: s,
  compact,
  value,
  setValue,
  submit,
  showFormat,
  disabled = false,
  // With a draft (the chat reply) the field subscribes to it and is the only thing that
  // renders on a keystroke; without one it is controlled by `value` (the home prompt).
  draft = null,
}) {
  const { t, active } = s;
  const capture = useCapture();
  const entryRef = useRef(null);
  const audio = useInlineCapture(capture, { finishedRef: entryRef });
  const text = useDraft(draft, value);
  const voice = useVoiceDraft(s, capture, text);
  const signed = !!s.auth.signed;
  const transcribing = voice.status === "transcribing";
  // Signed in, the microphone exists only where the Studio transcribes; a visitor
  // is sent to sign in first, since the recording needs an account to become text.
  const canRecord = !signed || !!s.features?.audio;
  // The home prompt's chosen topic is a chip at the start of the field; with it, the
  // prompt can go as it is (the title is the prompt) or with a direction typed after it.
  const topic = compact ? null : s.topic;
  const ready = !!(text.trim() || topic);
  const removeTopic = () => {
    s.clearTopic();
    entryRef.current?.querySelector("textarea")?.focus({ preventScroll: true });
  };
  // An art (single post): on the home, the template picked in the gallery and the aspect
  // ratio; in its chat, the layers "Edit with AI" scoped the next message to.
  const artFormat = !compact && STUDIO_FORMATS.find((format) => format.id === s.entry?.format);
  const art = artFormat?.art ? artFormat : null;
  const template = art ? s.entry.art?.template : null;
  const ratio = s.entry?.art?.aspect_ratio || ART_DEFAULT_RATIO;
  const artChat = compact && s.art?.enabled;
  // Personality shapes Reels scripts only.
  const reels = (compact ? s.current?.brief?.format : s.entry?.format) === DEFAULT_FORMAT;
  const scope = artChat ? s.art.scope : null;
  const scopeLayer = scope?.layers?.[0];
  const scopeName = !scope ? null : scope.layers.length > 1 ? t("artScopeMany", { count: scope.layers.length })
    : scopeLayer.name || (scopeLayer.type === "text" ? scopeLayer.text.split("\n")[0].slice(0, 32) : t(`artLayer_${scopeLayer.type}`));
  // Images (up to 5) ride on the prompt where an art takes them: from the + menu, dropped
  // on the composer or pasted into the field. Sending waits until every one is uploaded.
  const images = s.attachments;
  const attachable = !!images?.enabled && !disabled;
  const attachBusy = !!images?.enabled && images.busy;
  const fileRef = useRef(null);
  const dragDepth = useRef(0);
  const [dropping, setDropping] = useState(false);
  const hasFiles = (event) => [...(event.dataTransfer?.types || [])].includes("Files");
  const dropHandlers = {
    onDragEnter: (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepth.current += 1;
      setDropping(true);
    },
    onDragOver: (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = attachable ? "copy" : "none";
    },
    onDragLeave: (event) => {
      if (!hasFiles(event)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (!dragDepth.current) setDropping(false);
    },
    onDrop: (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepth.current = 0;
      setDropping(false);
      if (attachable) images.add(event.dataTransfer.files);
    },
  };
  const onPaste = (event) => {
    const files = [...(event.clipboardData?.files || [])].filter((file) => file.type.startsWith("image/"));
    if (!files.length || !attachable) return;
    event.preventDefault();
    images.add(files);
  };
  const attachReason = !signed ? "attachSignIn" : images?.enabled ? null : "attachOnlyArt";
  const placeholder = disabled ? "formatQuestion" : compact
    ? (scope ? "artReplyScopedPlaceholder" : artChat ? "artReplyPlaceholder" : "replyPlaceholder")
    : topic ? "promptTopicPlaceholder" : art ? "artPromptPlaceholder" : "promptPlaceholder";
  return (
    <Card className={`studio-composer${dropping ? " is-dropping" : ""}`} noBodyPadding cornerRadius="large" flat {...dropHandlers}>
      {dropping && (
        <div className={`studio-composer__drop${attachable ? "" : " is-unavailable"}`} aria-hidden="true">
          <Text.caption contrast={attachable ? 9 : 6} left={<IconPhotoPlus />}>
            {attachable ? t("attachDrop", { count: images.limit }) : t("attachDropUnavailable")}
          </Text.caption>
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept={ATTACHMENT_TYPES.join(",")}
        multiple
        hidden
        onChange={(event) => {
          const files = [...event.target.files];
          event.target.value = "";
          if (files.length) images.add(files);
        }}
      />
      {audio.open ? (
        <AudioRecorder capture={capture} t={t} onCancel={audio.cancel} />
      ) : (
        <>
          {images?.enabled && <ComposerAttachments studio={s} />}
          {capture.url && voice.status !== "idle" && (
            <div className="studio-composer__voice">
              <div className="studio-composer__voice-row">
                <audio
                  controls
                  aria-label={t("audioRecordPlayback")}
                  src={capture.url}
                />
                {voice.status === "failed" && (
                  <Button
                    preventLoading
                    variant="rounded"
                    color="overlay"
                    size="small"
                    icon={<IconRefresh />}
                    aria-label={t("retry")}
                    tooltip={t("retry")}
                    onClick={voice.retry}
                  />
                )}
                <Button
                  preventLoading
                  disabled={transcribing}
                  variant="rounded"
                  color="overlay"
                  size="small"
                  icon={<IconX />}
                  aria-label={t("audioDiscard")}
                  tooltip={t("audioDiscard")}
                  onClick={voice.discard}
                />
              </div>
              <Text.caption
                contrast={6}
                loading={transcribing}
                role={voice.status === "failed" ? "alert" : "status"}
              >
                {t(VOICE_STATUS[voice.status])}
              </Text.caption>
            </div>
          )}
          {(template || scope) && (
            <div className="studio-composer__context">
              {template && (
                <ArtToken
                  preview={template.document}
                  label={t("artTemplateToken", { name: template.name })}
                  removeLabel={t("artTemplateRemove")}
                  onRemove={s.clearArtTemplate}
                />
              )}
              {scope && (
                <ArtToken
                  icon={<IconFocus2 />}
                  label={t("artScopeToken", { name: scopeName })}
                  removeLabel={t("artScopeRemove")}
                  onRemove={s.art.clearScope}
                />
              )}
            </div>
          )}
          <div className={`studio-composer__entry${topic ? " has-token" : ""}`} ref={entryRef} onPaste={onPaste}>
            {topic && <TopicToken t={t} topic={topic} onRemove={removeTopic} />}
            <Input
              disabled={disabled}
              value={text}
              onInput={setValue}
              multiline
              rows={1}
              maxLength={12000}
              aria-label={compact ? t("sendMessage") : t("promptLabel")}
              placeholder={t(placeholder)}
              onKeyDown={(event) => {
                // Backspace at the very start of the field deletes the chip before it.
                if (
                  topic &&
                  event.key === "Backspace" &&
                  event.target.selectionStart === 0 &&
                  event.target.selectionEnd === 0
                ) {
                  event.preventDefault();
                  removeTopic();
                  return;
                }
                if (
                  event.key === "Enter" &&
                  !event.shiftKey &&
                  !event.nativeEvent.isComposing
                ) {
                  event.preventDefault();
                  if (!disabled && !transcribing && !attachBusy && ready && !active && !s.auth.loading)
                    submit();
                }
              }}
            />
          </div>
          <div className="studio-composer__toolbar">
            <div className="studio-composer__tools">
              <Menu
                items={[
                  {
                    label: t("attachImages"),
                    icon: <IconPhotoPlus />,
                    disabled: !attachable || images.full,
                    right: attachReason || images?.full ? (
                      <Text.caption contrast={5}>
                        {attachReason ? t(attachReason) : t("attachCount", { count: images.items.length, limit: images.limit })}
                      </Text.caption>
                    ) : undefined,
                    onClick: () => fileRef.current?.click(),
                  },
                  {
                    label: t("story"),
                    icon: <IconMicrophone />,
                    onClick: () => s.setModal("story"),
                  },
                  {
                    label: t("enrich"),
                    icon: <IconWand />,
                    onClick: () =>
                      setValue(
                        `${text}${text ? "\n\n" : ""}${t("enriched")}`,
                      ),
                  },
                ]}
              >
                <Button
                  disabled={disabled}
                  color="overlay"
                  variant="rounded"
                  size="medium"
                  icon={<IconPlus />}
                  aria-label={t("enrich")}
                  tooltip={t("enrich")}
                />
              </Menu>
              {/* Inside a project the format is fixed: no button, not even a disabled one. */}
              {showFormat && !compact && <FormatSelector studio={s} />}
              {art && showFormat && (
                <Menu
                  placement="top-start"
                  items={art.ratios.map((value) => ({
                    id: value,
                    label: `${t(`artRatio_${value.replace(":", "x")}`)} · ${value}`,
                    active: value === ratio,
                    onClick: () => s.setArtRatio(value),
                  }))}
                >
                  <Button
                    className="studio-plain-control"
                    color="overlay"
                    variant="rounded"
                    size="small"
                    startIcon={<IconAspectRatio />}
                    dropdown
                    aria-label={`${t("artRatio")}: ${ratio}`}
                  >
                    {ratio}
                  </Button>
                </Menu>
              )}
              {art && showFormat && (
                <Button
                  className="studio-templates studio-plain-control"
                  color="overlay"
                  variant="rounded"
                  size="small"
                  startIcon={<IconLayoutGrid />}
                  preventLoading
                  onClick={() => s.setModal({ type: "artGallery" })}
                >
                  {t("templates")}
                </Button>
              )}
              {/* Personality: how the active profile talks, which Reels scripts keep on the structure that goes viral. */}
              {reels && <PersonalityButton studio={s} />}
            </div>
            <div className="studio-row">
              {canRecord && (
                <Button
                  ref={audio.triggerRef}
                  preventLoading
                  disabled={disabled || !!active || transcribing}
                  className="studio-voice studio-plain-control"
                  variant="rounded"
                  color="overlay"
                  size="medium"
                  icon={<IconMicrophone />}
                  aria-label={t("audio")}
                  tooltip={t(signed ? "audio" : "audioSignIn")}
                  onClick={signed ? audio.start : s.goToLogin}
                />
              )}
              {compact && active ? (
                <Button
                  className="studio-submit"
                  variant="rounded"
                  color="inverse"
                  size="medium"
                  startIcon={<IconPlayerStopFilled />}
                  aria-label={t("cancel")}
                  onClick={s.cancelRun}
                >
                  {t("cancel")}
                </Button>
              ) : (
                <Button
                  className="studio-submit"
                  disabled={disabled || transcribing || attachBusy || !ready || s.auth.loading}
                  variant="rounded"
                  color="inverse"
                  size="medium"
                  endIcon={<IconArrowUp />}
                  aria-label={t(compact ? "sendMessage" : art ? "artSend" : "send")}
                  onClick={submit}
                >
                  {t(compact ? "sendShort" : art ? "artSend" : "send")}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
